import {injectable} from "inversify";
import { trace, FirebasePerformance, getPerformance } from "firebase/performance";
import {initializeApp} from "firebase/app";

export type SectionType = {
  name: string,
  total: number,
  current: number,
  finished: boolean,
}

@injectable()
export class SectionLoader {
  private sections: SectionType[] = [];

  isFinished(): boolean {
    return !this.getSections().find((s: SectionType) => !s.finished);
  }

  getCurrentSectionIndex(): number {
    const index = this.getSections().findIndex((s: SectionType) => !s.finished);

    return index > -1 ? index : this.getSections().length - 1;
  }

  getSections(): SectionType[] {
    return this.sections;
  }

  addSection(name: string, total: number = 1): this {
    this.sections.push({
      name,
      total,
      current: 0,
      finished: false,
    });

    return this;
  }

  advanceSection(name: string, step: number = 1): this {
    const section = this.getSection(name);
    section.current = Math.min(section.total, section.current + step);
    section.finished = section.current === section.total;

    return this;
  }

  finish(name: string): this {
    const section = this.getSection(name);
    section.finished = true;
    section.current = section.total;

    return this;
  }

  private getSection(name: string) {
    const section = this.sections.find(s => s.name === name);

    if (!section) {
      throw `Loading section '${name}' does not exist.`;
    }

    return section;
  }

  public async processSection<T>(
    name: string,
    callback: (advance: (step?: number) => void) => Promise<T>
  ): Promise<T> {
    console.time(`Loader::processSection::${name}`);
    const result = await callback((step: number = 1) => this.advanceSection(name, step));
    this.finish(name);
    console.timeEnd(`Loader::processSection::${name}`);

    return result;
  }
}